import React, { Suspense, lazy, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import CanvasBoundary from './three/CanvasBoundary';
import SectionHeading from './ui/SectionHeading';
import Reveal from './ui/Reveal';
import useInView from '../hooks/useInView';

import python from '../assets/python.svg';
import llm from '../assets/llm.svg';
import langchain from '../assets/langchain.svg';
import rag from '../assets/rag.svg';
import memgraph from '../assets/memgraph.svg';
import GO from '../assets/go.png';
import git from '../assets/github.png';
import aws from '../assets/aws.png';
import docker from '../assets/doc.png';
import Java from "../assets/java.png";
import spring from "../assets/spring-boot.png";
import sql from "../assets/sql.png";
import fastapi from "../assets/fastapi.svg";

const SkillsGlobe = lazy(() => import('./three/SkillsGlobe'));

const allSkills = [
    // AI & LLM Systems
    { name: 'Python', category: 'ai', src: python, alt: 'Python programming language icon', badge: 'AI & Data' },
    { name: 'LLMs & GenAI', category: 'ai', src: llm, alt: 'Large Language Models and Generative AI icon', badge: 'AI Core' },
    { name: 'LangChain & Agents', category: 'ai', src: langchain, alt: 'LangChain framework icon', badge: 'AI Agents' },
    { name: 'RAG & Vector DBs', category: 'ai', src: rag, alt: 'Retrieval Augmented Generation and Vector Databases icon', badge: 'Vector Search' },
    { name: 'Memgraph (Graph DB)', category: 'ai', src: memgraph, alt: 'Memgraph in-memory Graph Database icon', badge: 'Graph DB / RAG' },

    // Backend & Cloud
    { name: 'Java', category: 'backend', src: Java, alt: 'Java programming language icon', badge: 'Backend' },
    { name: 'Spring Boot', category: 'backend', src: spring, alt: 'Spring Boot framework icon', badge: 'Microservices' },
    { name: 'FastAPI', category: 'backend', src: fastapi, alt: 'FastAPI Python web framework icon', badge: 'APIs' },
    { name: 'Go (Golang)', category: 'backend', src: GO, alt: 'Go programming language icon', badge: 'Systems' },
    { name: 'AWS Cloud', category: 'cloud', src: aws, alt: 'Amazon Web Services cloud icon', badge: 'Cloud' },
    { name: 'Docker', category: 'cloud', src: docker, alt: 'Docker containerization icon', badge: 'DevOps' },
    { name: 'SQL & Databases', category: 'backend', src: sql, alt: 'SQL database and relational queries icon', badge: 'Database' },
    { name: 'Git & GitHub', category: 'cloud', src: git, alt: 'Git version control and GitHub icon', badge: 'Tooling' },
];

const categories = [
    { id: 'ai', label: 'AI & Machine Learning' },
    { id: 'backend', label: 'Backend & Systems' },
    { id: 'cloud', label: 'Cloud & DevOps' },
];

const Skills = () => {
    // Hovering a category highlights its skills on the globe.
    const [activeCategory, setActiveCategory] = useState('all');
    const reducedMotion = useReducedMotion();
    const [globeRef, globeInView] = useInView();

    return (
        <section name='skills' className='relative w-full py-24 sm:py-32 border-t border-slate-800/60'>
            <div className='max-w-[1160px] w-full mx-auto px-6 sm:px-8'>
                <SectionHeading
                    eyebrow="Skills"
                    title="Technologies I work with"
                    subtitle="Technologies, frameworks, and AI tools I specialize in."
                />

                <div className='grid lg:grid-cols-[1fr_1.1fr] gap-8 items-stretch'>
                    {/* 3D skills globe */}
                    <Reveal>
                        <div ref={globeRef} className='card relative h-[340px] sm:h-[460px] lg:h-full lg:min-h-[460px] overflow-hidden' aria-hidden="true">
                            <CanvasBoundary>
                                <Suspense fallback={null}>
                                    <SkillsGlobe
                                        skills={allSkills}
                                        activeCategory={activeCategory}
                                        active={globeInView}
                                        reducedMotion={reducedMotion}
                                    />
                                </Suspense>
                            </CanvasBoundary>
                        </div>
                    </Reveal>

                    {/* Skills grouped by category */}
                    <div className='flex flex-col gap-4'>
                        {categories.map((cat, i) => (
                            <Reveal key={cat.id} delay={i * 0.06}>
                                <div
                                    className='card p-5 sm:p-6 hover:border-slate-600 transition-colors'
                                    onMouseEnter={() => setActiveCategory(cat.id)}
                                    onMouseLeave={() => setActiveCategory('all')}
                                >
                                    <h3 className='text-slate-100 font-semibold mb-4'>{cat.label}</h3>
                                    <div className='flex flex-wrap gap-2.5'>
                                        {allSkills
                                            .filter((skill) => skill.category === cat.id)
                                            .map((skill) => (
                                                <span
                                                    key={skill.name}
                                                    className='inline-flex items-center gap-2 rounded-md border border-slate-700/80 bg-[#0b1120] px-3 py-2 text-sm text-slate-300'
                                                >
                                                    <img src={skill.src} alt={skill.alt} loading="lazy" width="18" height="18" className='h-[18px] w-[18px] object-contain' />
                                                    {skill.name}
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Skills;
